import { t } from 'i18next';
import Papa from 'papaparse';
import React from 'react';
import { cn } from '../common/utils';
import { PanelHeader } from '../components/designer/panels/PanelHeader';
import FileUpload from '../components/FileUpload';
import { Select } from '../components/form/Select';
import { SKUData } from './sku-profiling';
import { useSKU } from './SKUContext';
import { useTranslation } from 'react-i18next';

const SKUDataUploader: React.FC = () => {
  const { t } = useTranslation('app');
  const { selectedSKU, setSelectedSKU, skuData, setSkuData } = useSKU();
  const [selectedFileName, setSelectedFileName] = React.useState<string>(
    () => localStorage.getItem('selectedSKUFileName') || '',
  );
  const [searchTerm, setSearchTerm] = React.useState('');

  const handleClear = () => {
    setSelectedFileName('');
    localStorage.removeItem('selectedSKUFileName');
    setSkuData([]);
    setSelectedSKU(null);
  };

  // Load and parse CSV data
  const loadCSVData = async (file: File) => {
    Papa.parse(file, {
      header: true,
      complete: results => {
        const parsedData = results.data.map((row: any) => {
          const numericRow: any = {
            ...row,
            order_line_count: Number(row.order_line_count),
            quantity_ordered: Number(row.quantity_ordered),
            percent_rank: Number(row.percent_rank),
            cumulative_percent_rank: Number(row.cumulative_percent_rank),
            total_days_ordered: Number(row.total_days_ordered),
            total_weeks_ordered: Number(row.total_weeks_ordered),
            max_daily_lines_count: Number(row.max_daily_lines_count),
            min_daily_lines_count: Number(row.min_daily_lines_count),
            avg_daily_lines_count: Number(row.avg_daily_lines_count),
            max_weekly_lines_count: Number(row.max_weekly_lines_count),
            min_weekly_lines_count: Number(row.min_weekly_lines_count),
            avg_weekly_lines_count: Number(row.avg_weekly_lines_count),
            max_lines: Number(row.max_lines),
            max_quantity: Number(row.max_quantity),
            line_accuracy: Number(row.line_accuracy),
            rank: String(row.rank),
          };

          // Dynamically convert weekly metrics
          Object.keys(row).forEach(key => {
            if (key.startsWith('w') && key.includes('_')) {
              if (key.includes('abc')) {
                numericRow[key] = row[key]; // Keep as string for ABC classification
              } else {
                numericRow[key] = Number(row[key]);
              }
            }
          });

          return numericRow;
        });
        setSkuData(parsedData as SKUData[]);
      },
    });
  };

  const filteredSKUs = React.useMemo(() => {
    return skuData.filter(sku =>
      sku?.sku?.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [skuData, searchTerm]);

  return (
    <PanelHeader className="flex items-center gap-4">
      <FileUpload
        accept=".csv"
        onChange={file => {
          if (file) {
            setSelectedFileName(file.name);
            loadCSVData(file);
          }
        }}
        className={cn('rounded', 'px-4 py-2', 'bg-app-panel-dark/30', 'flex-1')}
      >
        {selectedFileName || t`Select File`}
      </FileUpload>
      {/* <label className="text-sm font-medium">{t`Select Item:`}</label> */}
      <div className="flex flex-1 items-center gap-2">
        <input
          type="text"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          placeholder={t`Search SKU...`}
          className="bg-app-panel-dark/30 flex-1 rounded px-2 py-1"
        />
        <Select
          value={selectedSKU || ''}
          onChange={e => setSelectedSKU(e.target.value)}
          className="w-full"
        >
          <option value="">{t`All Items`}</option>
          {filteredSKUs.map(sku => (
            <option key={sku.sku} value={sku.sku}>
              {sku.sku}
            </option>
          ))}
        </Select>
      </div>
    </PanelHeader>
  );
};

export default SKUDataUploader;
